import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "@/app/providers/AxiosProvider";
import { RECRUITER_KEYS } from "./keys";

/**
 * The recruiter's structured communication style controls.
 *
 * Dimension and band labels are served by the API from the same config that
 * holds the prompt fragments, so the UI copy can never drift from the prompt.
 */
export interface StyleBandOption {
  band: number;
  label: string;
}

export interface StyleDimension {
  label: string;
  bands: StyleBandOption[];
}

export interface CommunicationStyle {
  formality: number;
  length: number;
  energy: number;
  vocabulary: number;
  bluntness: number;
  custom_guidance: string;
  dimensions: Record<string, StyleDimension>;
}

export type CommunicationStyleValues = Omit<CommunicationStyle, "dimensions">;

export const COMMUNICATION_STYLE_KEY = "communication-style";

export default function useCommunicationStyle(recruiterId?: string) {
  const api = useAxios();
  const queryClient = useQueryClient();

  const query = useQuery<CommunicationStyle>({
    queryKey: [COMMUNICATION_STYLE_KEY, recruiterId],
    queryFn: async () => {
      const res = await api.get(`/recruiter/${recruiterId}/communication-style`);
      return res.data;
    },
    enabled: Boolean(recruiterId),
    refetchOnWindowFocus: false,
  });

  const mutation = useMutation({
    mutationFn: async (values: CommunicationStyleValues) => {
      const res = await api.post(
        `/recruiter/${recruiterId}/communication-style`,
        values
      );
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([COMMUNICATION_STYLE_KEY, recruiterId], data);
      // The base prompt is recomposed server-side on save, and it lives on
      // /me/settings rather than /me.
      queryClient.invalidateQueries({ queryKey: RECRUITER_KEYS.settings() });
    },
  });

  return { query, mutation };
}
